import {
  AddFormReponseRequest,
  FormDetails,
  FormResponseDetails,
  UpdateFormResponseRequest,
} from '@shared/models/form/form.model';
import { OfflineFormContextResponse } from '@shared/models/offline/offline-submission.model';
import {
  ServiceRequestFormPlacementDetail,
  SubmitServiceRequestFormRequest,
} from '@shared/models/service-request/service-request.model';
import {
  resolveServiceRequestFormSubmissionStatus,
  SERVICE_REQUEST_FORM_SUBMISSION_STATUS,
} from '../../constants/service-request-form-submission-status.constants';
import { getStaticFormFieldMappings } from '@shared/services/forms/static-form-field-mappings';
import {
  StaticFormFieldMappings,
  StaticServiceRequestFormResponse,
} from './service-request-form-response.mapper.util';
import { withStandardServiceRequestFormQuestions } from './service-request-form.util';

export function isPlacementSubmitted(placement: ServiceRequestFormPlacementDetail): boolean {
  return placement.submissionStatus?.trim().toUpperCase() === SERVICE_REQUEST_FORM_SUBMISSION_STATUS.COMPLETED;
}

export function isPlacementEditable(
  placementOrStatus?: ServiceRequestFormPlacementDetail | string | null,
): boolean {
  const submissionStatus = typeof placementOrStatus === 'object' && placementOrStatus != null
    ? placementOrStatus.submissionStatus
    : placementOrStatus;

  switch (submissionStatus?.trim().toUpperCase()) {
    case SERVICE_REQUEST_FORM_SUBMISSION_STATUS.COMPLETED:
    case 'RETIRED':
      return false;
    default:
      return true;
  }
}

export type FormPlacementSubmissionStatusPillModifier = 'completed' | 'in-progress';

export function resolveFormPlacementSubmissionStatusPillModifier(
  submissionStatus?: string | null,
): FormPlacementSubmissionStatusPillModifier | null {
  switch (normalizeFormPlacementSubmissionStatus(submissionStatus)) {
    case SERVICE_REQUEST_FORM_SUBMISSION_STATUS.COMPLETED:
      return 'completed';
    case 'IN_PROGRESS':
    case 'DRAFT':
      return 'in-progress';
    default:
      return null;
  }
}

function normalizeFormPlacementSubmissionStatus(submissionStatus?: string | null): string {
  return submissionStatus?.trim().toUpperCase().replace(/-/g, '_').replace(/\s+/g, '_') ?? '';
}

export function parsePlacementResponseJson(
  responseJson?: string | null,
): Partial<StaticServiceRequestFormResponse> | null {
  if (!responseJson?.trim()) {
    return null;
  }

  try {
    return JSON.parse(responseJson) as Partial<StaticServiceRequestFormResponse>;
  } catch {
    return null;
  }
}

export function parsePlacementFormDefinitionJson(
  formDefinitionJson: string,
): FormDetails | null {
  if (!formDefinitionJson?.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(formDefinitionJson) as FormDetails;
    if (!Array.isArray(parsed.formQuestions)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function mapPlacementToStaticFormResponse(
  placement: ServiceRequestFormPlacementDetail,
  fieldMappings?: StaticFormFieldMappings,
): StaticServiceRequestFormResponse {
  const mappings = fieldMappings ?? getStaticFormFieldMappings(placement.formCode);
  const parsed = parsePlacementResponseJson(placement.responseJson);

  return {
    formId: placement.formId,
    formCode: placement.formCode,
    formName: placement.formName,
    inspectionNo: placement.inspectionNo ?? null,
    formType: parsed?.formType ?? 'STANDARD',
    fieldMappings: mappings,
    responseId: parsed?.responseId,
    responseCode: parsed?.responseCode,
    responseDateSubmitted: placement.submittedAt ?? parsed?.responseDateSubmitted,
    respondent: placement.respondentName
      ? {
        respondentUserId: placement.submittedBy ?? parsed?.respondent?.respondentUserId ?? '',
        respondentName: placement.respondentName,
        respondentEmail: parsed?.respondent?.respondentEmail ?? '',
        respondentContactNumber: parsed?.respondent?.respondentContactNumber ?? '',
      }
      : parsed?.respondent,
    answers: parsed?.answers ?? [],
  };
}

export function mapPlacementToDynamicForm(
  placement: ServiceRequestFormPlacementDetail,
): FormDetails | FormResponseDetails {
  const definition = parsePlacementFormDefinitionJson(placement.formDefinitionJson);
  const parsedResponse = parsePlacementResponseJson(placement.responseJson);
  const base: FormDetails = {
    formId: placement.formId,
    formCode: placement.formCode,
    formName: placement.formName,
    formDescription: definition?.formDescription ?? '',
    formType: definition?.formType ?? '',
    formQuestions: definition?.formQuestions ?? [],
  };

  if (!parsedResponse?.answers?.length && !isPlacementSubmitted(placement)) {
    return withStandardServiceRequestFormQuestions(base);
  }

  const answersByQuestionId = new Map(
    (parsedResponse?.answers ?? []).map((answer) => [answer.questionId, answer]),
  );

  const mergedQuestions = base.formQuestions.map((question) => {
    const answerRow = answersByQuestionId.get(question.formQuestionId);
    if (!answerRow) {
      return question;
    }

    return {
      ...question,
      answer: answerRow.answer,
      responseQuestionId: answerRow.responseQuestionId,
      assets: answerRow.assets,
    };
  });

  const response: FormResponseDetails = {
    ...base,
    formQuestions: mergedQuestions,
    responseId: parsedResponse?.responseId ?? 0,
    responseCode: parsedResponse?.responseCode ?? '',
    responseDateSubmitted: placement.submittedAt ?? parsedResponse?.responseDateSubmitted ?? '',
    respondent: placement.respondentName
      ? {
        respondentUserId: placement.submittedBy ?? '',
        respondentName: placement.respondentName,
        respondentEmail: parsedResponse?.respondent?.respondentEmail ?? '',
        respondentContactNumber: parsedResponse?.respondent?.respondentContactNumber ?? '',
      }
      : parsedResponse?.respondent,
  };

  return withStandardServiceRequestFormQuestions(response);
}

export function buildSubmitServiceRequestFormRequest(
  placementId: number,
  clientId: string,
  serviceRequestId: number,
  responseJson: string,
  complete: boolean,
  inspectorId: number,
  options?: Pick<
    SubmitServiceRequestFormRequest,
    'workNarrative' | 'outcomeReasonCodeId' | 'outcome' | 'stepResults'
  >,
): SubmitServiceRequestFormRequest {
  return {
    clientId,
    serviceRequestId,
    placementId,
    inspectorId,
    responseJson,
    status: resolveServiceRequestFormSubmissionStatus(complete),
    ...options,
  };
}

export function buildPlacementResponseJsonFromStaticSnapshot(
  snapshot: StaticServiceRequestFormResponse,
): string {
  return JSON.stringify(snapshot);
}

export function buildPlacementResponseJsonFromDynamicInsert(
  payload: AddFormReponseRequest,
): string {
  return JSON.stringify({
    formId: payload.formId,
    questions: payload.questions,
  });
}

export function buildPlacementResponseJsonFromDynamicUpdate(
  payload: UpdateFormResponseRequest,
): string {
  return JSON.stringify({
    responseId: payload.responseId,
    questions: payload.questions,
  });
}

export function mapOfflineContextToPlacementDetail(
  context: OfflineFormContextResponse,
  formCode: string,
): ServiceRequestFormPlacementDetail {
  return {
    placementId: context.formPlacementId,
    serviceRequestId: context.serviceRequestId,
    formId: context.formId,
    formCode,
    formName: context.formName,
    formDefinitionJson: context.formDefinitionJson,
    submissionStatus: context.submissionStatus,
    responseJson: context.responseJson ?? null,
    isCompletionForm: false,
    stepResults: [],
    availableOutcomes: [],
    boundTypeSteps: [],
  };
}

export function mapPlacementToOfflineFormSnapshot(
  placement: ServiceRequestFormPlacementDetail,
  isStaticForm: boolean,
  fieldMappings?: StaticFormFieldMappings,
): {
  snapshot: FormDetails | FormResponseDetails;
  mode: 'create' | 'edit';
} {
  const hasResponse = isPlacementSubmitted(placement) || !!placement.responseJson?.trim();
  const mode = hasResponse ? 'edit' : 'create';

  if (isStaticForm) {
    const staticResponse = mapPlacementToStaticFormResponse(placement, fieldMappings);
    return {
      snapshot: staticResponse as unknown as FormDetails,
      mode,
    };
  }

  return {
    snapshot: mapPlacementToDynamicForm(placement),
    mode,
  };
}
